import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import {
  loadKioskVisitors,
  selectKiosk,
  signInVisitorOnKiosk,
  signOutVisitorOnKiosk,
} from '../data/kiosk.duck';
import { KioskVisitor, KioskVisitorSignInState } from '../data/kiosk-service';
import { VisitorRow } from './VisitorRow';
import { AddVisitorPanel } from './AddVisitorPanel';

const REFRESH_INTERVAL_MS = 30_000;

export function KioskVisitorList() {
  const dispatch = useDispatch();
  const kiosk = useSelector(selectKiosk);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    dispatch(loadKioskVisitors());
    const id = window.setInterval(() => dispatch(loadKioskVisitors()), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [dispatch]);

  const handleSignIn = (id: number) => dispatch(signInVisitorOnKiosk({ id }));
  const handleSignOut = (id: number) => dispatch(signOutVisitorOnKiosk({ id }));

  const signedIn = kiosk.visitors.filter(v => v.signInState === KioskVisitorSignInState.CHECKED_IN);
  const signedOut = kiosk.visitors.filter(v => v.signInState === KioskVisitorSignInState.CHECKED_OUT);
  const yetToSignIn = kiosk.visitors.filter(v => v.signInState === KioskVisitorSignInState.NONE);

  const renderGroup = (title: string, visitors: KioskVisitor[]) => (
    <Group>
      <GroupHeader><BodyLarge weight={600}>{title} ({visitors.length})</BodyLarge></GroupHeader>
      {visitors.length === 0 ? (
        <EmptyGroup><BodyRegular>None</BodyRegular></EmptyGroup>
      ) : (
        <List>
          {visitors.map(v => (
            <VisitorRow
              key={v.id}
              visitor={v}
              onSignIn={handleSignIn}
              onSignOut={handleSignOut}
            />
          ))}
        </List>
      )}
    </Group>
  );

  return (
    <Container>
      <SectionHeader>
        <HeadlineSmall>Today’s visitors</HeadlineSmall>
        <AddButton type="button" onClick={() => setShowAdd(true)}>+ Add visitor</AddButton>
      </SectionHeader>

      {showAdd && (
        <AddVisitorPanel onClose={() => setShowAdd(false)} />
      )}

      {kiosk.lastError && <ErrorBanner>{kiosk.lastError}</ErrorBanner>}

      {kiosk.visitors.length === 0 && !kiosk.loading ? (
        <Empty>
          <BodyRegular>No visitors yet today.</BodyRegular>
        </Empty>
      ) : (
        <>
          {renderGroup('Yet to sign in', yetToSignIn)}
          {renderGroup('Signed in', signedIn)}
          {renderGroup('Signed out', signedOut)}
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AddButton = styled.button`
  height: 56px;
  padding: 0 24px;
  border-radius: 8px;
  background: ${Colours.blue};
  color: white;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupHeader = styled.div`
  padding: 4px 4px 8px;
`;

const EmptyGroup = styled.div`
  padding: 16px;
  text-align: center;
  border: 1px dashed ${Colours.mildGrey};
  border-radius: 8px;
  color: ${Colours.darkGrey};
`;

const Empty = styled.div`
  padding: 32px;
  text-align: center;
  border: 1px dashed ${Colours.mildGrey};
  border-radius: 12px;
  color: ${Colours.darkGrey};
`;

const ErrorBanner = styled.div`
  padding: 12px 16px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 8px;
`;
